import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { completeRampChecklist, getRampChecklist, initRampChecklist, patchOperationItem, signRampChecklist } from "../../api/rampChecklistApi";
import { downloadChecklistPdf } from "./downloadChecklistPdf";
import { anchorLocalDate, isFallbackTz, resolveTz, utcToLocalDateTime } from "./rampChecklistTime";

// 与后端模板项数一致（backend RAMP_CHECKLIST_ITEM_COUNT）；仅在尚未加载到 items 时兜底显示分母。
export const RAMP_CHECKLIST_ITEM_COUNT = 23;

/**
 * 过站检查单的状态与动作集中在这里：加载 / 逐条保存（带乐观锁回退） / 签名 / 完成 / 下载 PDF。
 * 移动端整页（ChecklistPage）和桌面抽屉标签页（RampChecklistTab）共用，避免两处各写一遍。
 */
export default function useRampChecklist(legId, { readOnly = false, onCompleted } = {}) {
    const { hasPermission } = useAuth();
    // 桌面端只读标签页强制只看不改：既不渲染编辑控件，也不调用会隐式建单的 init。
    // 填写（生成/打卡/签名/完成）用独立的 ramp_checklist:update，与 flight_operation:update 解耦。
    const canEdit = !readOnly && hasPermission("ramp_checklist:update");
    const numericLegId = Number(legId);

    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [signOpen, setSignOpen] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [exporting, setExporting] = useState(false);

    const reload = useCallback(async () => {
        const res = await getRampChecklist(numericLegId);
        setChecklist(res.data);
        return res.data;
    }, [numericLegId]);

    useEffect(() => {
        if (!Number.isFinite(numericLegId)) {
            setLoading(false);
            setNotFound(true);
            return undefined;
        }
        let cancelled = false;
        const bootstrap = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                // 有更新权限的人进入即幂等生成；只读权限的人只取现有内容。
                const res = canEdit ? await initRampChecklist(numericLegId) : await getRampChecklist(numericLegId);
                if (!cancelled) setChecklist(res.data);
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    message.error("加载检查单失败，请稍后重试");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        bootstrap();
        return () => {
            cancelled = true;
        };
    }, [numericLegId, canEdit]);

    const applyServerRecord = useCallback((data) => {
        setChecklist((prev) => {
            if (!prev) return prev;
            const updated = { item_id: data.id, category: data.category, lock_version: data.lock_version, attributes: data.attributes };
            if (prev.header && prev.header.item_id === data.id) {
                return { ...prev, header: updated };
            }
            return { ...prev, items: prev.items.map((item) => (item.item_id === data.id ? updated : item)) };
        });
    }, []);

    const patchRecord = useCallback(
        async (itemId, fields, lockVersion) => {
            try {
                const res = await patchOperationItem(itemId, fields, lockVersion);
                applyServerRecord(res.data);
            } catch (err) {
                const status = err?.response?.status;
                if (status === 409) {
                    message.warning("这一项刚被人改过，请刷新后再改");
                } else if (status === 403) {
                    message.error("没有修改权限");
                } else {
                    message.error("保存失败，请重试");
                }
                // 回退到服务端真实值，避免界面与服务端显示不一致（本期不做本地暂存）
                await reload().catch(() => {});
                throw err;
            }
        },
        [applyServerRecord, reload],
    );

    const flight = checklist?.flight || {};
    const header = checklist?.header || null;
    const headerAttrs = header?.attributes || {};
    const items = checklist?.items || [];

    const tzId = useMemo(() => resolveTz(headerAttrs.tz), [headerAttrs.tz]);
    const tzFallback = isFallbackTz(headerAttrs.tz);
    const anchorDate = useMemo(() => anchorLocalDate(flight.std, tzId), [flight.std, tzId]);

    const totalCount = items.length || RAMP_CHECKLIST_ITEM_COUNT;
    const checkedCount = items.filter((item) => item.attributes?.checked).length;
    const completed = !!headerAttrs.completed;
    const hasSignature = !!headerAttrs.signature_data;

    const fmt = useCallback((utcIso) => (utcIso ? utcToLocalDateTime(utcIso, tzId, "YYYY-MM-DD HH:mm") : "—"), [tzId]);

    const handleSignConfirm = useCallback(
        async (dataUrl) => {
            setSignOpen(false);
            // 走后端"签名"专用动作：签字人由后端按登录人写入，前端只提交手写图。
            try {
                const res = await signRampChecklist(numericLegId, dataUrl);
                setChecklist(res.data);
            } catch (err) {
                const backendMsg = err?.response?.data?.detail;
                message.error(typeof backendMsg === "string" ? backendMsg : "保存签名失败，请重试");
            }
        },
        [numericLegId],
    );

    const handleComplete = useCallback(async () => {
        if (!hasSignature) {
            message.warning("需要先签名才能完成");
            return;
        }
        setCompleting(true);
        // 真正的"先签名才能完成"由后端把关；这里只做即时提示，避免无谓请求。
        try {
            const res = await completeRampChecklist(numericLegId);
            setChecklist(res.data);
            message.success("已标记为完成");
            onCompleted?.();
        } catch (err) {
            const backendMsg = err?.response?.data?.detail;
            message.error(typeof backendMsg === "string" ? backendMsg : "标记完成失败，请重试");
        } finally {
            setCompleting(false);
        }
    }, [hasSignature, numericLegId, onCompleted]);

    const handleExport = useCallback(async () => {
        if (!checklist) return;
        setExporting(true);
        // 页内隐藏渲染检查单并截图拼成 PDF，直接触发下载，不再经过浏览器打印对话框。
        try {
            await downloadChecklistPdf({ checklist, tzId });
        } catch (err) {
            message.error("下载 PDF 失败，请重试");
        } finally {
            setExporting(false);
        }
    }, [checklist, tzId]);

    return {
        canEdit,
        checklist,
        loading,
        notFound,
        flight,
        header,
        headerAttrs,
        items,
        tzId,
        tzFallback,
        anchorDate,
        totalCount,
        checkedCount,
        completed,
        hasSignature,
        fmt,
        patchRecord,
        signOpen,
        setSignOpen,
        handleSignConfirm,
        completing,
        handleComplete,
        exporting,
        handleExport,
    };
}
